import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { lazy } from "react";
import { useSelector, useDispatch } from "react-redux";

import "./App.css";
import "./components/reusable/TextButton.css";
import renderWithFallback from "./components/reusable/loading/renderWithFallback";
import type { RootState } from "./state/store";
import { setError } from "./state/apis/slices/errorSlice";
import { setAlert } from "./state/apis/slices/alertSlice";

import textRouter from "./components/text/textRouter";
import adminRouter from "./components/admin/adminRouter";
import userRouter from "./components/user/userRouter";
import homeChefRouter from "./components/homeChef/homeChefRouter";
import formsRouter from "./components/forms/formsRouter";
import volunteersRouter from "./components/volunteers/volunteersRouter";
import cboRouter from "./components/cbo/cboRouter";
import volunteerCheckInRouter from "./components/volunteer-check-in/volunteerCheckInRouter";
import doorfrontRouter from "./components/doorfront/doorfrontRouter";

// public home chef app page (no sign in required)
const HomeChefApp = lazy(() => import("./components/homeChef/HomeChefApp"));
const SalesforceNotFound = lazy(
  () => import("./components/error/SalesforceNotFound"),
);
const Home = lazy(() => import("./components/Home"));
const Header = lazy(() => import("./components/header/Header"));
const PageNotFound = lazy(() => import("./components/error/PageNotFound"));
const ForgotPassword = lazy(() => import("./components/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("./components/user/ResetPassword"));
const DeleteD4JAccount = lazy(
  () => import("./components/user/DeleteD4JAccount"),
);
const ParseReport = lazy(() => import("./components/ParseReport"));

export const router = createBrowserRouter([
  {
    path: "/",
    element: renderWithFallback(<Header />),
    errorElement: renderWithFallback(<PageNotFound />),
    children: [
      {
        index: true,
        element: renderWithFallback(<Home />),
      },

      {
        path: "forgot-password",
        element: renderWithFallback(<ForgotPassword />),
      },
      {
        path: "delete-data/:email",
        element: renderWithFallback(<DeleteD4JAccount />),
      },

      {
        path: "reset-password/:token",
        element: renderWithFallback(<ResetPassword />),
      },
      { path: "home-chef-app", element: renderWithFallback(<HomeChefApp />) },
      { path: "data", element: renderWithFallback(<ParseReport />) },

      textRouter,
      adminRouter,
      userRouter,
      homeChefRouter,
      volunteersRouter,
      cboRouter,
      { path: "404", element: renderWithFallback(<SalesforceNotFound />) },
    ],
  },
  formsRouter,
  volunteerCheckInRouter,
  doorfrontRouter,
]);

const App = () => {
  const error = useSelector((state: RootState) => state.error.message);
  const alert = useSelector((state: RootState) => state.alert.message);

  const dispatch = useDispatch();

  const renderError = () => {
    setTimeout(() => dispatch(setError("")), 5000);
    return <div className="error">{error}</div>;
  };

  const renderAlert = () => {
    setTimeout(() => dispatch(setAlert("")), 5000);
    return <div className="error alert">{alert}</div>;
  };

  return (
    <div className="app">
      <RouterProvider router={router} />
      {error && renderError()}
      {alert && renderAlert()}
    </div>
  );
};

export default App;
